"use client"

import { cn } from "@/utils/classnames"
import { XMarkIcon } from "@heroicons/react/24/outline"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import clsx from "clsx"
import * as React from "react"

const Drawer = DialogPrimitive.Root

const DrawerTrigger = DialogPrimitive.Trigger

const DrawerPortal = DialogPrimitive.Portal

const DrawerClose = DialogPrimitive.Close

type DrawerOverlayProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  nested?: boolean
}

const DrawerOverlay = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Overlay>, DrawerOverlayProps>(
  ({ className, nested, ...props }, ref) => (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-[101] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
        { "bg-overlay": !nested }
      )}
      {...props}
    />
  )
)
DrawerOverlay.displayName = DialogPrimitive.Overlay.displayName

type DrawerContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  nested?: boolean
}

const DrawerContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DrawerContentProps>(
  ({ className, children, nested = false, ...props }, ref) => (
    <DrawerPortal>
      <DialogPrimitive.Content
        style={{
          zIndex: 101,
        }}
        id="test-drawer"
        ref={ref}
        onPointerDownOutside={e => {
          // stop the dialog closing when clicking the toast dismiss button
          if ((e.target as HTMLElement).closest('[id="toast-dismiss-button"]')) {
            e.preventDefault()
          }
        }}
        className={cn(
          "fixed right-0 top-0 z-50 flex h-full w-full max-w-[800px] flex-col border-l focus:outline-none focus:ring-0 focus-visible:outline-none",
          "gap-4 border-bg-3 bg-bg-1 p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out sm:p-3",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:slide-out-to-right-1/2 data-[state=open]:slide-in-from-right-1/2",
          className
        )}
        {...props}>
        {children}
      </DialogPrimitive.Content>
    </DrawerPortal>
  )
)
DrawerContent.displayName = DialogPrimitive.Content.displayName

const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={clsx("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
)
DrawerHeader.displayName = "DrawerHeader"

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={clsx("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
)
DrawerFooter.displayName = "DrawerFooter"

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={clsx("text-lg font-semibold leading-none tracking-tight text-h5-lg", className)}
    {...props}
  />
))
DrawerTitle.displayName = DialogPrimitive.Title.displayName

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={clsx("text-muted-foreground text-sm", className)} {...props} />
))
DrawerDescription.displayName = DialogPrimitive.Description.displayName

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
}
